import { useSelector } from 'react-redux';

import { useSellerOnboardingJson } from './use-seller-setting';
import { SINGLE_LABEL_VOLUME } from '../constants/seller-constants';
import Global from '../store/global-selectors';

const SMALL_TRIALER_THRESHOLD = 50;

type ShippingVolumeCategory = {
  isSmallTrialer: boolean;
  isMediumLargeTrialer: boolean;
  hasNoVolumeData: boolean;
};

/**
 * Hook to categorize users based on their expected shipping volume from registration.
 * Used to separate experiences for small vs medium/large trialers.
 *
 * Prioritizes shipmentsPerMonth from seller data (e.g., "1") over onboarding JSON.
 * When shipmentsPerMonth is "1", user is considered a small trialer (single label seller).
 * When shipmentsPerMonth is any other value, user is considered medium/large trialer.
 * When data is missing (undefined or null), we fall back to onboarding JSON threshold (<= 50 shipments/month).
 * When no data exists at all, we default to treating them as small merchants.
 * @example
 * const { isSmallTrialer, isMediumLargeTrialer, hasNoVolumeData } = useShippingVolumeCategory();
 *
 * if (isSmallTrialer) {
 *   // Show experience for small merchants (shipmentsPerMonth === "1" or <= 50 shipments/month or no data)
 * } else if (isMediumLargeTrialer) {
 *   // Show experience for users with > 50 shipments/month
 * }
 */
export const useShippingVolumeCategory = (): ShippingVolumeCategory => {
  const shipmentsPerMonth = useSelector(Global.selectSellerShipmentsPerMonth);
  const onboardingData = useSellerOnboardingJson();
  const shippingVolume = onboardingData?.SHIPPING_VOLUME?.expectedShippingVolume;

  // If shipmentsPerMonth is set, use that to determine category
  if (shipmentsPerMonth) {
    const isSmallSeller = shipmentsPerMonth === SINGLE_LABEL_VOLUME;
    return {
      isSmallTrialer: isSmallSeller,
      isMediumLargeTrialer: !isSmallSeller,
      hasNoVolumeData: false,
    };
  }

  // Fall back to onboarding JSON logic
  const hasNoVolumeData = shippingVolume === undefined || shippingVolume === null;
  // When we have no data, default to small merchant experience
  const isMediumLargeTrialer = !hasNoVolumeData && shippingVolume > SMALL_TRIALER_THRESHOLD;
  const isSmallTrialer = hasNoVolumeData || shippingVolume <= SMALL_TRIALER_THRESHOLD;

  return {
    isSmallTrialer,
    isMediumLargeTrialer,
    hasNoVolumeData,
  };
};
